/**
 * Impersonation Service
 *
 * Core service for managing user impersonation state and lifecycle. Provides reactive signals for impersonation state
 * consumed by all Module Federation remotes.
 *
 * Key Features:
 *
 * - Reactive state management using Angular signals
 * - Encrypted token storage in localStorage
 * - Multi-tab synchronization (<200ms)
 * - Backend validation for session integrity
 * - Module-specific permission application
 *
 * @packageDocumentation
 */

import { HttpClient } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, Injector, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AuthStateService } from '../auth/auth-core/services/auth-state.service';
import { normalizeStringArray } from '../auth/authorization/utils/claims-parser.util';
import {
  ImpersonationActivityData,
  ImpersonationLogoutRequest,
  ImpersonationLogoutResponse,
  ImpersonationStartRequest,
  ImpersonationStartResponse,
  ImpersonationSyncEvent,
} from '../models/impersonation-api.model';
import { ImpersonationError, ImpersonationErrorCode } from '../models/impersonation-error.model';
import {
  EMPTY_IMPERSONATION_STATE,
  ImpersonationState,
  ImpersonationUser,
  MODULE_CONFIG,
  STORAGE_KEYS,
} from '../models/impersonation-state.model';
import { EncryptedToken } from '../models/impersonation-token.model';
import { IndexedDBStore } from '../storage/indexed-db.store';
import {
  ASSESSMENT_DB_NAME,
  getUserAssessmentDbName,
  ASSESSMENT_KEYED_TABLES,
  ASSESSMENT_SCHEMA_VERSION,
  ASSESSMENT_TABLE_NAMES,
  ASSESSMENT_TABLES,
} from '../storage/schema-registry';
import { ImpersonationSyncService } from './impersonation-sync.service';
import { TokenEncryptionService } from './token-encryption.service';
import { UserContextService } from './user-context.service';

/**
 * Service for managing user impersonation state
 *
 * This is the primary service for impersonation. It:
 *
 * - Manages impersonation state via reactive signals
 * - Handles start/end impersonation lifecycle
 * - Coordinates with backend API for token generation/validation
 * - Synchronizes state across browser tabs
 * - Applies module-specific permission logic
 *
 * @example
 *   ```typescript
 *   const impersonationService = inject(ImpersonationService);
 *
 *   // Check if impersonating
 *   const isImpersonating = impersonationService.isImpersonating();
 *
 *   // Get current state
 *   const state = impersonationService.impersonationState();
 *
 *   // Start impersonation
 *   impersonationService.startImpersonation('user-123').subscribe();
 *
 *   // End impersonation
 *   impersonationService.endImpersonation().subscribe();
 *   ```;
 */
@Injectable({
  providedIn: 'root',
})
export class ImpersonationService {
  // Injected dependencies
  private readonly http = inject(HttpClient);
  private readonly encryptionService = inject(TokenEncryptionService);
  private readonly syncService = inject(ImpersonationSyncService);
  private readonly authState = inject(AuthStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private get assessmentStore(): IndexedDBStore {
    return IndexedDBStore.forDatabase(getUserAssessmentDbName());
  }

  /**
   * Writable signal for impersonation state
   *
   * This is the source of truth for impersonation state. Updated when impersonation starts, ends, or syncs from another
   * tab.
   */
  private readonly _impersonationState = signal<ImpersonationState>(EMPTY_IMPERSONATION_STATE);

  /**
   * Current impersonation state (reactive signal)
   *
   * All Module Federation remotes can consume this signal to react to impersonation state changes.
   *
   * @example
   *   ```typescript
   *   const state = impersonationService.impersonationState();
   *   if (state.isActive) {
   *     console.log('Impersonating:', state.impersonatedUser);
   *   }
   *   ```;
   */
  public readonly impersonationState = this._impersonationState.asReadonly();

  /**
   * Writable signal indicating whether the impersonation session is currently being ended/cleared. Consumed by unsaved
   * changes guards/directives to bypass prompts during redirect.
   */
  public readonly isEndingImpersonation = signal<boolean>(false);

  /**
   * Computed signal - true if impersonation is currently active
   *
   * Derived from impersonationState for convenience.
   */
  public readonly isImpersonating = computed(() => this._impersonationState().isActive);

  /**
   * Computed signal - Original admin user (null if not impersonating)
   *
   * Derived from impersonationState for convenience.
   */
  public readonly originalAdmin = computed(() => this._impersonationState().originalAdmin);

  /**
   * Computed signal - Impersonated user (null if not impersonating)
   *
   * Derived from impersonationState for convenience.
   */
  public readonly impersonatedUser = computed(() => this._impersonationState().impersonatedUser);

  private static readonly ASSESSMENT_TRACKING_TABLE = ASSESSMENT_TABLE_NAMES.IMPERSONATION_SESSION_ASSESSMENTS;

  constructor() {
    // Subscribe to sync events from other tabs
    this.syncService.syncEvents$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event.type === 'start' && event.token) {
        // Skip if this tab is the originator (adminTokenId already set on our state).
        // dispatchSync() manually emits to the same-tab Subject, but we don't want to
        // clobber the full state (which includes adminTokenId) with the partial sync state.
        const currentState = this._impersonationState();
        if (currentState.isActive && currentState.adminTokenId != null) {
          return;
        }
        // Another tab started impersonation - sync state
        this.syncImpersonationStart(event);
      } else if (event.type === 'end') {
        // Another tab ended impersonation - sync state
        this.syncImpersonationEnd();
      }
    });

    // Restore impersonation state from localStorage on page refresh
    this.restoreImpersonationState();
  }

  /**
   * Starts impersonation of a target user
   *
   * Flow:
   *
   * 1. Validate admin has User.Simulate entitlement
   * 2. Prevent nested impersonation
   * 3. Call backend POST /api/impersonation/start
   * 4. Encrypt original admin token
   * 5. Store encrypted token in localStorage
   * 6. Update impersonationState signal
   * 7. Dispatch sync event to other tabs
   *
   * @example
   *   ```typescript
   *   this.impersonationService.startImpersonation('nurse-456')
   *     .pipe(
   *       catchError(error => {
   *         this.showError(error.userMessage);
   *         return EMPTY;
   *       })
   *     )
   *     .subscribe(() => {
   *       console.log('Impersonation started successfully');
   *     });
   *   ```;
   *
   * @param targetUserId - User ID to impersonate
   * @param targetUserTypeId - User type applied to navigation and route guards during impersonation
   * @param adminApiBaseUrl - Admin API base URL
   * @returns Observable<void> - Completes when impersonation is fully initialized
   * @throws ImpersonationError if validation fails or backend call fails
   */
  startImpersonation(targetUserId: string, targetUserTypeId: number, adminApiBaseUrl: string = ''): Observable<void> {
    // 1. Get current admin session and validate
    const currentSession = this.authState.getSession();
    if (!currentSession.customAccessToken) {
      return throwError(
        () => new ImpersonationError(ImpersonationErrorCode.INVALID_TOKEN, 'No valid authentication token found')
      );
    }

    if (!Number.isInteger(targetUserTypeId)) {
      return throwError(
        () => new ImpersonationError(ImpersonationErrorCode.USER_NOT_FOUND, 'Target user type is missing or invalid')
      );
    }

    const originalUserTypeId = this.getUserContext().userTypeId();

    // Parse current token to check permissions
    const currentAdminClaims = this.encryptionService.parseJWTClaims(currentSession.customAccessToken);
    const adminEntitlements = normalizeStringArray(currentAdminClaims.entitlement);

    // Check for User.Simulate permission directly from token claims
    if (!adminEntitlements.includes('User.Simulate')) {
      return throwError(
        () =>
          new ImpersonationError(ImpersonationErrorCode.MISSING_ENTITLEMENT, 'Admin lacks User.Simulate entitlement')
      );
    }

    // 2. Prevent nested impersonation
    if (this._impersonationState().isActive) {
      return throwError(
        () =>
          new ImpersonationError(
            ImpersonationErrorCode.ALREADY_IMPERSONATING,
            'Already impersonating a user - nested impersonation not allowed'
          )
      );
    }

    const currentAdminToken = currentSession.customAccessToken;

    // 3. Call backend POST /api/Impersonation/token
    const request: ImpersonationStartRequest = { impersonatedUserEmail: targetUserId };

    return this.http.post<ImpersonationStartResponse>(`${adminApiBaseUrl}/api/Impersonation/token`, request).pipe(
      switchMap(async (response) => {
        // 4. Parse new impersonation token
        const impersonationClaims = this.encryptionService.parseJWTClaims(response.accessToken);
        // 5. Encrypt original admin token
        const encrypted = await this.encryptionService.encryptToken(currentAdminToken, currentAdminClaims.oi_tkn_id);

        // 6. Store encrypted token in localStorage
        localStorage.setItem(STORAGE_KEYS.ENCRYPTED_ADMIN_TOKEN, JSON.stringify(encrypted));
        // Store adminTokenId in sessionStorage (not localStorage) to keep the decryption key
        // material separate from the ciphertext — an attacker reading localStorage cannot
        // derive the AES-GCM key without also reading the tab's sessionStorage.
        sessionStorage.setItem(STORAGE_KEYS.ADMIN_TOKEN_ID, currentAdminClaims.oi_tkn_id);

        // 7. Build impersonation state
        const originalAdmin: ImpersonationUser = {
          userId: currentAdminClaims.userId,
          userTypeId: originalUserTypeId ?? undefined,
          userName: currentAdminClaims.userName,
          userEmail: currentAdminClaims.userEmail,
          clientId: currentAdminClaims.clientId,
          roleId: currentAdminClaims.roleId,
          entitlements: normalizeStringArray(currentAdminClaims.entitlement),
        };

        const impersonatedUser: ImpersonationUser = {
          userId: impersonationClaims.userId,
          userTypeId: targetUserTypeId,
          userName: impersonationClaims.userName,
          userEmail: impersonationClaims.userEmail,
          clientId: impersonationClaims.clientId,
          roleId: impersonationClaims.roleId,
          entitlements: normalizeStringArray(impersonationClaims.entitlement),
        };

        // Convert expiresIn (seconds) to ISO 8601 expiry timestamp
        const expiresAt = new Date(Date.now() + response.expiresIn * 1000).toISOString();

        const newState: ImpersonationState = {
          isActive: true,
          originalAdmin,
          impersonatedUser,
          sessionId: impersonationClaims.oi_prst ?? null,
          adminTokenId: currentAdminClaims.oi_tkn_id,
          impersonationTokenId: impersonationClaims.oi_tkn_id ?? null,
          startedAt: new Date().toISOString(),
          expiresAt,
        };

        // 9. Update impersonation state signal
        this._impersonationState.set(newState);

        // 10. Update auth session with impersonation token
        this.authState.updateSession({
          customAccessToken: response.accessToken,
          permissions: normalizeStringArray(impersonationClaims.entitlement),
        });

        this.persistUserTypeContext(originalUserTypeId, targetUserTypeId);
        this.getUserContext().setUserTypeId(targetUserTypeId);

        // 11. Dispatch sync event to other tabs
        const syncEvent: ImpersonationSyncEvent = {
          type: 'start',
          sessionId: impersonationClaims.oi_prst,
          timestamp: new Date().toISOString(),
          token: response.accessToken,
          originalAdmin,
          impersonatedUserTypeId: targetUserTypeId,
        };
        this.syncService.dispatchSync(syncEvent);

        return;
      }),
      catchError((error) => {
        // Handle HTTP or encryption errors
        if (error instanceof ImpersonationError) {
          return throwError(() => error);
        }

        // Check for specific HTTP error statuses
        if (error.status === 403) {
          return throwError(
            () =>
              new ImpersonationError(
                ImpersonationErrorCode.MISSING_ENTITLEMENT,
                'Backend rejected impersonation request - User.Simulate required',
                error
              )
          );
        }

        if (error.status === 404) {
          return throwError(
            () =>
              new ImpersonationError(
                ImpersonationErrorCode.USER_NOT_FOUND,
                `Target user ${targetUserId} not found`,
                error
              )
          );
        }

        return throwError(
          () =>
            new ImpersonationError(
              ImpersonationErrorCode.BACKEND_ERROR,
              `Failed to start impersonation: ${error.message || 'Unknown error'}`,
              error
            )
        );
      })
    );
  }

  /**
   * Ends the current impersonation session and restores admin privileges
   *
   * Flow:
   *
   * 1. Validate currently impersonating
   * 2. Decrypt original admin token from localStorage
   * 3. Restore admin session locally (no backend call required)
   * 4. Return activity data for caller to log
   * 5. Clear encrypted token from localStorage
   * 6. Reset impersonationState signal
   * 7. Dispatch sync event to other tabs
   *
   * @example
   *   ```typescript
   *   this.impersonationService.endImpersonation()
   *     .pipe(
   *       catchError(error => {
   *         this.showError(error.userMessage);
   *         return EMPTY;
   *       })
   *     )
   *     .subscribe((activityData) => {
   *       // Log activity using shell's ActivityLogService
   *       console.log('Impersonation ended, admin session restored');
   *     });
   *   ```;
   *
   * @param adminApiBaseUrl - Optional base URL for the admin API (required for logout API call)
   * @returns Observable with activity data for logging - caller should log this to audit system
   * @throws ImpersonationError if not impersonating or decryption fails
   */
  endImpersonation(adminApiBaseUrl: string = ''): Observable<ImpersonationActivityData> {
    // Set ending flag to prevent navigation/alert interruptions during redirect
    this.isEndingImpersonation.set(true);

    // 1. Validate currently impersonating
    const currentState = this._impersonationState();
    if (!currentState.isActive) {
      this.isEndingImpersonation.set(false);
      return throwError(
        () =>
          new ImpersonationError(
            ImpersonationErrorCode.NOT_IMPERSONATING,
            'Cannot end impersonation when not currently impersonating'
          )
      );
    }

    // 2. Get adminTokenId - try state first, then fall back to sessionStorage
    // This handles cases where the in-memory state was not fully restored after navigation
    // but the sessionStorage still contains the valid adminTokenId from the originating tab.
    let adminTokenId = currentState.adminTokenId;
    if (!adminTokenId) {
      adminTokenId = sessionStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN_ID);
    }

    if (!adminTokenId) {
      this.isEndingImpersonation.set(false);
      return throwError(
        () =>
          new ImpersonationError(
            ImpersonationErrorCode.SESSION_INVALID,
            'No admin token ID found in impersonation state'
          )
      );
    }

    // 3. Get encrypted original admin token from localStorage
    const encryptedTokenJson = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_ADMIN_TOKEN);
    if (!encryptedTokenJson) {
      this.isEndingImpersonation.set(false);
      return throwError(
        () =>
          new ImpersonationError(ImpersonationErrorCode.ENCRYPTION_ERROR, 'Original admin token not found in storage')
      );
    }

    let encryptedToken: EncryptedToken;
    try {
      encryptedToken = JSON.parse(encryptedTokenJson) as EncryptedToken;
    } catch (error) {
      this.isEndingImpersonation.set(false);
      return throwError(
        () =>
          new ImpersonationError(
            ImpersonationErrorCode.ENCRYPTION_ERROR,
            'Failed to parse encrypted admin token',
            error instanceof Error ? error : undefined
          )
      );
    }

    // Capture impersonated user info for logout API call
    const impersonatedUserEmail = currentState.impersonatedUser?.userEmail || '';
    const impersonatedTokenId = currentState.impersonationTokenId || '';

    // 3. Decrypt the stored admin token and restore the session locally
    return new Observable((observer) => {
      (async () => {
        try {
          // IMPORTANT: Decrypt and restore admin session FIRST
          // This ensures any subsequent HTTP calls (including the logout API) use the admin token
          // The logout API doesn't need the impersonated user's Authorization header -
          // it uses the impersonatedUserEmail and impersonatedTokenId from the request body
          const adminToken = await this.encryptionService.decryptToken(encryptedToken, adminTokenId);
          const adminClaims = this.encryptionService.parseJWTClaims(adminToken);

          // Restore admin session BEFORE making the logout API call
          this.authState.updateSession({
            customAccessToken: adminToken,
            permissions: normalizeStringArray(adminClaims.entitlement),
          });

          const originalUserTypeId =
            currentState.originalAdmin?.userTypeId ?? this.getStoredUserTypeId(STORAGE_KEYS.ORIGINAL_USER_TYPE_ID);
          if (originalUserTypeId !== null) {
            this.getUserContext().setUserTypeId(originalUserTypeId);
          }

          // Now call backend API to invalidate the impersonation token
          // The request body contains the impersonated user's email and token ID
          // The backend uses client credentials to authenticate with auth central
          if (impersonatedUserEmail && impersonatedTokenId) {
            const request: ImpersonationLogoutRequest = {
              impersonatedUserEmail,
              impersonatedTokenId,
            };
            try {
              const response = await this.http
                .post<ImpersonationLogoutResponse>(`${adminApiBaseUrl}/api/Impersonation/logout`, request)
                .toPromise();
            } catch (logoutError) {
              // Log the error but continue with local cleanup
              // This ensures the user can still end impersonation even if backend call fails
            }
          }

          // Prepare activity data for caller to log
          const activityData = this.prepareActivityData(currentState);

          // Clear encrypted token from localStorage and key ID from sessionStorage
          localStorage.removeItem(STORAGE_KEYS.ENCRYPTED_ADMIN_TOKEN);
          sessionStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN_ID);
          this.clearUserTypeContext();

          // Clear all local assessment data created during impersonation.
          // This is best-effort and should not block ending impersonation.
          await this.clearImpersonationAssessmentIndexedDbData();

          // Reset impersonation state
          this._impersonationState.set(EMPTY_IMPERSONATION_STATE);

          // Dispatch sync event to other tabs
          const syncEvent: ImpersonationSyncEvent = {
            type: 'end',
            timestamp: new Date().toISOString(),
          };
          this.syncService.dispatchSync(syncEvent);

          observer.next(activityData);
          observer.complete();
        } catch (error) {
          this.isEndingImpersonation.set(false);
          if (error instanceof ImpersonationError) {
            observer.error(error);
          } else {
            observer.error(
              new ImpersonationError(
                ImpersonationErrorCode.BACKEND_ERROR,
                `Failed to end impersonation: ${error instanceof Error ? error.message : 'Unknown error'}`,
                error instanceof Error ? error : undefined
              )
            );
          }
        }
      })();
    });
  }

  /**
   * Validates the current impersonation session with backend
   *
   * Useful for periodic health checks to ensure the session hasn't been revoked server-side.
   *
   * @returns Observable<boolean> - true if session is valid, false otherwise
   */
  validateSession(): Observable<boolean> {
    // TODO: Implement if needed for session health checks
    if (!this._impersonationState().isActive) {
      return throwError(
        () =>
          new ImpersonationError(
            ImpersonationErrorCode.NOT_IMPERSONATING,
            'Cannot validate session when not impersonating'
          )
      );
    }

    // Backend call: GET /api/impersonation/validate
    return EMPTY; // Placeholder
  }

  /**
   * Gets the current impersonation state (non-reactive snapshot)
   *
   * Use the impersonationState signal for reactive updates. This method is for one-time checks.
   *
   * @returns Current impersonation state
   */
  getCurrentState(): ImpersonationState {
    return this._impersonationState();
  }

  /**
   * Determines if current module should apply impersonation
   *
   * Per specification: Only web.assessment applies impersonation. All other modules (admin, shell, template, audit)
   * ignore it.
   *
   * @example
   *   ```typescript
   *   const shouldApply = impersonationService.shouldApplyImpersonation('web.assessment');
   *   if (shouldApply && this.isImpersonating()) {
   *     // Use impersonated user's permissions
   *     return this.impersonatedUser()!.entitlements;
   *   } else {
   *     // Use original admin's permissions
   *     return this.originalAdmin()!.entitlements;
   *   }
   *   ```;
   *
   * @param moduleName - Module name (e.g., 'web.assessment')
   * @returns True if module should use impersonated permissions
   */
  shouldApplyImpersonation(moduleName: string): boolean {
    return MODULE_CONFIG.applyImpersonation.includes(moduleName);
  }

  /**
   * Prepares activity data for logging by the caller. Extracts user information and calculates impersonation duration.
   *
   * @param currentState - Current impersonation state before ending
   * @returns Activity data object for logging
   */
  private prepareActivityData(currentState: ImpersonationState): ImpersonationActivityData {
    // Extract user information from state
    const impersonatedUserId = currentState.impersonatedUser?.userId || 'unknown';
    const impersonatedUserName = currentState.impersonatedUser?.userName || 'Unknown User';

    // Calculate impersonation duration (ceiling: any seconds count as full minute)
    const startTime = currentState.startedAt ? new Date(currentState.startedAt) : new Date();
    const endTime = new Date();
    const durationMs = endTime.getTime() - startTime.getTime();
    const durationMins = Math.ceil(durationMs / 60000);
    const hours = Math.floor(durationMins / 60);
    const mins = durationMins % 60;

    // Format duration as "X hours Y mins" or "Y mins"
    const durationStr =
      hours > 0
        ? `${hours} hour${hours > 1 ? 's' : ''} ${mins} min${mins !== 1 ? 's' : ''}`
        : `${mins} min${mins !== 1 ? 's' : ''}`;

    return {
      impersonatedUserId,
      impersonatedUserName,
      durationMinutes: durationMins,
      durationStr,
      startTime: currentState.startedAt || new Date().toISOString(),
      sessionId: currentState.sessionId,
    };
  }

  /**
   * Tracks an assessment as touched during an active impersonation session. This enables deterministic cleanup on both
   * assessment exit and end-impersonation.
   */
  async trackImpersonationAssessment(assessmentId: number): Promise<void> {
    const isActive = this._impersonationState().isActive;
    const hasEncryptedAdminToken = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_ADMIN_TOKEN) != null;
    const isValidAssessmentId = Number.isFinite(assessmentId);

    if ((!isActive && !hasEncryptedAdminToken) || !isValidAssessmentId) {
      return;
    }

    try {
      const store = this.assessmentStore;
      await store.initializeWithSchema(ASSESSMENT_SCHEMA_VERSION, ASSESSMENT_TABLES);
      await store.put(ImpersonationService.ASSESSMENT_TRACKING_TABLE, {
        assessmentId,
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.warn('[ImpersonationService] Failed to track impersonation assessment', error);
    }
  }

  /**
   * Clears all impersonation-only cached data for a single assessment. This should be called when exiting an assessment
   * while still impersonating.
   */
  async clearImpersonationAssessmentData(assessmentId: number): Promise<void> {
    if (!Number.isFinite(assessmentId)) {
      return;
    }

    await this.clearImpersonationAssessmentIndexedDbData([assessmentId]);
  }

  private async clearImpersonationAssessmentIndexedDbData(targetAssessmentIds?: number[]): Promise<void> {
    try {
      const store = this.assessmentStore;
      await store.initializeWithSchema(ASSESSMENT_SCHEMA_VERSION, ASSESSMENT_TABLES);

      const trackedAssessmentIds = await this.resolveImpersonationAssessmentIds(store, targetAssessmentIds);

      if (trackedAssessmentIds.length === 0) {
        return;
      }

      await this.deleteIdRemapsForAssessments(store, trackedAssessmentIds);

      // These tables were migrated from an assessmentId-keyed to a memberAppointmentId-keyed
      // primary key; resolve the real key via the assessmentId index before deleting.
      const memberAppointmentKeyedTables: readonly string[] = [
        ASSESSMENT_TABLE_NAMES.CLAIMS,
        ASSESSMENT_TABLE_NAMES.CEDIS,
        ASSESSMENT_TABLE_NAMES.UCC,
        ASSESSMENT_TABLE_NAMES.MEDICATIONS,
      ];

      for (const assessmentId of trackedAssessmentIds) {
        for (const table of ASSESSMENT_KEYED_TABLES) {
          if (memberAppointmentKeyedTables.includes(table)) {
            const records = await store.filterByIndex<{ memberAppointmentId: number }>(
              table,
              'assessmentId',
              assessmentId
            );
            for (const record of records) {
              await store.delete(table, record.memberAppointmentId);
            }
            continue;
          }
          await store.delete(table, assessmentId);
        }

        await this.deleteQueuedOperationsForAssessment(store, ASSESSMENT_TABLE_NAMES.PENDING_SYNC, assessmentId);
        await this.deleteQueuedOperationsForAssessment(store, ASSESSMENT_TABLE_NAMES.SYNC_DEAD_LETTER, assessmentId);
        await store.delete(ImpersonationService.ASSESSMENT_TRACKING_TABLE, assessmentId);
      }
    } catch (error) {
      console.warn('[ImpersonationService] Failed to clear impersonation assessment cache data', error);
    }
  }

  private async resolveImpersonationAssessmentIds(
    store: IndexedDBStore,
    targetAssessmentIds?: number[]
  ): Promise<number[]> {
    const explicitIds = (targetAssessmentIds ?? []).filter((id) => Number.isFinite(id));
    const trackedRows = await store.getAll<{ assessmentId: number }>(ImpersonationService.ASSESSMENT_TRACKING_TABLE);
    const trackedIds = trackedRows.map((row) => row.assessmentId).filter((id) => Number.isFinite(id));
    const unresolvedAssessmentRemaps = await store.filter<{
      localId: string;
      resourceType: string;
      serverId: string | null;
    }>(
      ASSESSMENT_TABLE_NAMES.ID_REMAP,
      (entry) => entry.resourceType === 'assessment' && entry.serverId == null && /^-\d+$/.test(entry.localId)
    );
    const unresolvedIds = unresolvedAssessmentRemaps
      .map((entry) => Number.parseInt(entry.localId, 10))
      .filter((id) => Number.isFinite(id));

    return Array.from(new Set([...explicitIds, ...trackedIds, ...unresolvedIds]));
  }

  private async collectAssessmentIdsFromKeyedTables(store: IndexedDBStore): Promise<number[]> {
    const discoveredIds = new Set<number>();

    for (const tableName of ASSESSMENT_KEYED_TABLES) {
      const rows = await store.getAll<{ assessmentId?: unknown }>(tableName);
      for (const row of rows) {
        const candidate = row?.assessmentId;
        if (typeof candidate === 'number' && Number.isFinite(candidate)) {
          discoveredIds.add(candidate);
        } else if (typeof candidate === 'string') {
          const parsed = Number.parseInt(candidate, 10);
          if (Number.isFinite(parsed)) {
            discoveredIds.add(parsed);
          }
        }
      }
    }

    return Array.from(discoveredIds);
  }

  private async deleteIdRemapsForAssessments(store: IndexedDBStore, assessmentIds: number[]): Promise<void> {
    const assessmentIdSet = new Set(assessmentIds.map((id) => String(id)));
    const localQuestionnaireResponseIds = new Set<string>();

    for (const assessmentId of assessmentIds) {
      const response = await store.getByKey<Record<string, unknown>>(ASSESSMENT_TABLE_NAMES.RESPONSE, assessmentId);
      const responseId = response?.['id'];
      if (typeof responseId === 'string' && /^local-[0-9a-f-]{36}$/i.test(responseId)) {
        localQuestionnaireResponseIds.add(responseId);
      }
    }

    const remaps = await store.getAll<{
      localId: string;
      serverId: string | null;
      resourceType: string;
    }>(ASSESSMENT_TABLE_NAMES.ID_REMAP);

    for (const remap of remaps) {
      if (
        assessmentIdSet.has(remap.localId) ||
        (remap.serverId != null && assessmentIdSet.has(remap.serverId)) ||
        localQuestionnaireResponseIds.has(remap.localId)
      ) {
        await store.delete(ASSESSMENT_TABLE_NAMES.ID_REMAP, remap.localId);
      }
    }
  }

  private async deleteQueuedOperationsForAssessment(
    store: IndexedDBStore,
    tableName: string,
    assessmentId: number
  ): Promise<void> {
    const records = await store.filter<{ id?: number; assessmentId: number }>(
      tableName,
      (record) => record.assessmentId === assessmentId
    );

    for (const record of records) {
      if (record.id != null) {
        await store.delete(tableName, record.id);
      }
    }
  }

  /**
   * Restores impersonation state on page refresh. If an encrypted admin token exists in localStorage, the current
   * session token is treated as an active impersonation token and state is rebuilt.
   */
  private restoreImpersonationState(): void {
    try {
      const encryptedTokenJson = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_ADMIN_TOKEN);
      const adminTokenId = sessionStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN_ID);
      if (!encryptedTokenJson) {
        return;
      }

      // Current session token is the impersonation token (set before refresh)
      const currentSession = this.authState.getSession();
      if (!currentSession.customAccessToken) {
        return;
      }

      const claims = this.encryptionService.parseJWTClaims(currentSession.customAccessToken);
      const impersonatedUserTypeId = this.getStoredUserTypeId(STORAGE_KEYS.IMPERSONATED_USER_TYPE_ID);
      const impersonatedUser: ImpersonationUser = {
        userId: claims.userId,
        userTypeId: impersonatedUserTypeId ?? undefined,
        userName: claims.userName,
        userEmail: claims.userEmail,
        clientId: claims.clientId,
        roleId: claims.roleId,
        entitlements: normalizeStringArray(claims.entitlement),
      };

      this._impersonationState.set({
        isActive: true,
        originalAdmin: null,
        impersonatedUser,
        sessionId: claims.oi_prst ?? null,
        adminTokenId: adminTokenId ?? null,
        impersonationTokenId: claims.oi_tkn_id ?? null,
        startedAt: new Date().toISOString(),
        expiresAt: new Date(claims.exp * 1000).toISOString(),
      });

      if (impersonatedUserTypeId !== null) {
        queueMicrotask(() => {
          if (this._impersonationState().isActive) {
            this.getUserContext().setUserTypeId(impersonatedUserTypeId);
          }
        });
      }
    } catch {
      // If restore fails, clean up to avoid a broken state
      localStorage.removeItem(STORAGE_KEYS.ENCRYPTED_ADMIN_TOKEN);
      sessionStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN_ID);
      this.clearUserTypeContext();
    }
  }

  /**
   * Syncs impersonation start from another tab
   *
   * Called when storage event indicates another tab started impersonation.
   *
   * @param syncEvent - Full sync event from other tab
   */
  private syncImpersonationStart(syncEvent: ImpersonationSyncEvent): void {
    try {
      const impersonationToken = syncEvent.token!;
      // Parse JWT claims
      const claims = this.encryptionService.parseJWTClaims(impersonationToken);
      const impersonatedUserTypeId = syncEvent.impersonatedUserTypeId;

      // Build impersonation state from token claims
      const impersonatedUser: ImpersonationUser = {
        userId: claims.userId,
        userTypeId: impersonatedUserTypeId,
        userName: claims.userName,
        userEmail: claims.userEmail,
        clientId: claims.clientId,
        roleId: claims.roleId,
        entitlements: normalizeStringArray(claims.entitlement),
      };

      // We don't have adminTokenId in cross-tab sync (the encrypted token is in the originating tab's
      // sessionStorage), so we read originalAdmin directly from the sync event payload.
      const originalAdmin: ImpersonationUser | null = syncEvent.originalAdmin ?? null;

      const newState: ImpersonationState = {
        isActive: true,
        originalAdmin,
        impersonatedUser,
        sessionId: claims.oi_prst ?? null,
        adminTokenId: null, // Not available in cross-tab sync; end-impersonation must originate from the starting tab
        impersonationTokenId: claims.oi_tkn_id ?? null,
        startedAt: new Date().toISOString(),
        expiresAt: new Date(claims.exp * 1000).toISOString(),
      };

      // Update state
      this._impersonationState.set(newState);

      // Update auth session with impersonation token
      this.authState.updateSession({
        customAccessToken: impersonationToken,
        permissions: normalizeStringArray(claims.entitlement),
      });

      if (impersonatedUserTypeId !== undefined) {
        this.persistUserTypeContext(originalAdmin?.userTypeId ?? null, impersonatedUserTypeId);
        this.getUserContext().setUserTypeId(impersonatedUserTypeId);
      }
    } catch (error) {
      console.error('Failed to sync impersonation start:', error);
    }
  }

  /**
   * Syncs impersonation end from another tab
   *
   * Called when storage event indicates another tab ended impersonation.
   */
  private syncImpersonationEnd(): void {
    try {
      this.isEndingImpersonation.set(true);
      // Clear local impersonation token artifacts if present.
      localStorage.removeItem(STORAGE_KEYS.ENCRYPTED_ADMIN_TOKEN);
      sessionStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN_ID);
      this.clearUserTypeContext();

      // Reset state
      this._impersonationState.set(EMPTY_IMPERSONATION_STATE);

      // Best-effort local assessment cleanup for both originating and non-originating tabs.
      // This must run even when encrypted token is already absent.
      void this.clearImpersonationAssessmentIndexedDbData().catch((error) => {
        console.warn('[ImpersonationService] Cross-tab impersonation cleanup failed', error);
      });

      // Note: We don't restore the admin token here because we can't decrypt it
      // without the session ID. The user will need to refresh or the originating
      // tab should have broadcasted the fresh admin token (enhancement for future).
    } catch (error) {
      console.error('Failed to sync impersonation end:', error);
      // Fail safe: still reset state
      this._impersonationState.set(EMPTY_IMPERSONATION_STATE);
    }
  }

  /**
   * Clears impersonation state without calling the backend API
   *
   * This method should be called during logout (manual or session timeout) to ensure impersonation state is properly
   * cleaned up. Unlike endImpersonation(), this method:
   *
   * - Does NOT call the backend logout API (session is already ending)
   * - Does NOT attempt to restore the admin session (user is logging out anyway)
   * - Simply clears all impersonation storage and resets state
   *
   * Use Cases:
   *
   * - Manual logout while impersonating
   * - Session timeout during impersonation
   * - Auth service cleanup during logout flow
   *
   * @example
   *   ```typescript
   *   // In your auth/logout service:
   *   logout(): void {
   *     // Clear impersonation state first (if any)
   *     this.impersonationService.clearImpersonationState();
   *
   *     // Then proceed with normal logout
   *     this.authService.logout();
   *   }
   *   ```;
   *   n;
   */
  clearImpersonationState(): void {
    // Only proceed if there's impersonation state to clear
    const hasEncryptedToken = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_ADMIN_TOKEN);
    const hasAdminTokenId = sessionStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN_ID);
    const hasImpersonatedUserTypeId = sessionStorage.getItem(STORAGE_KEYS.IMPERSONATED_USER_TYPE_ID);
    const isActive = this._impersonationState().isActive;

    if (!hasEncryptedToken && !hasAdminTokenId && !hasImpersonatedUserTypeId && !isActive) {
      // Nothing to clear
      return;
    }

    this.isEndingImpersonation.set(true);

    // Clear encrypted admin token from localStorage
    localStorage.removeItem(STORAGE_KEYS.ENCRYPTED_ADMIN_TOKEN);

    // Clear admin token ID from sessionStorage
    sessionStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN_ID);
    this.clearUserTypeContext();

    // Reset impersonation state signal
    this._impersonationState.set(EMPTY_IMPERSONATION_STATE);

    // Dispatch sync event to other tabs so they also clear their state
    const syncEvent: ImpersonationSyncEvent = {
      type: 'end',
      timestamp: new Date().toISOString(),
    };
    this.syncService.dispatchSync(syncEvent);
  }

  /**
   * Clears impersonation state and best-effort assessment cache data. Intended for logout/session-expiry flows where
   * backend end-impersonation API is not called.
   */
  async clearImpersonationStateAndData(): Promise<void> {
    await this.clearImpersonationAssessmentIndexedDbData();
    this.clearImpersonationState();
  }

  private getUserContext(): UserContextService {
    return this.injector.get(UserContextService);
  }

  private persistUserTypeContext(originalUserTypeId: number | null, impersonatedUserTypeId: number): void {
    if (originalUserTypeId !== null) {
      sessionStorage.setItem(STORAGE_KEYS.ORIGINAL_USER_TYPE_ID, String(originalUserTypeId));
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER_TYPE_ID);
    }
    sessionStorage.setItem(STORAGE_KEYS.IMPERSONATED_USER_TYPE_ID, String(impersonatedUserTypeId));
  }

  private getStoredUserTypeId(key: string): number | null {
    const storedValue = sessionStorage.getItem(key);
    if (storedValue === null) {
      return null;
    }

    const userTypeId = Number(storedValue);
    return Number.isInteger(userTypeId) ? userTypeId : null;
  }

  private clearUserTypeContext(): void {
    sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER_TYPE_ID);
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATED_USER_TYPE_ID);
  }

  /** Cleanup on service destruction */
  ngOnDestroy(): void {
    // takeUntilDestroyed handles the sync subscription; nothing additional to clean up.
  }
}
